<!--
  HelpLayout.vue -- chrome for the help surface, both presentations.

  WHY THIS EXISTS. Help is documentation, not an app screen. Jake, on the
  shipped surface: "when opening a help page in a new tab, we don't need
  all of the nav links. The intent is to have the stand-alone help page
  read like a static document." App.vue's `currentLayout` therefore stops
  resolving a help route's `meta.layout`, so help no longer renders inside
  DistributorLayout (WORK / REPORTING / ACCOUNT sidebar, top bar,
  impersonation banner) or StorefrontLayout.

  WHY NOT JUST PublicLayout. Because "no app shell" and "nothing at all"
  are different, and the difference matters here: help is NOT only reached
  in a new tab. Three call sites navigate into it in the SAME tab via
  `$router.push('/distributor/help/owner')` --
  `pages/distributor/account/OnboardingScreen.vue` (three cards) and
  `pages/distributor/reporting/ReportBuilder.vue`. Dropped into a bare
  PublicLayout from one of those, a distributor has no sidebar, no top bar
  and no link at all: the browser Back button is the only way back into
  the product. Jake's requirement removes the nav LINKS, not the reader's
  bearings.

  So: a masthead. The ThreadFlow wordmark, which is also the way home.
  ONE link, deliberately -- a static document has a masthead, and this is
  the same minimal-chrome idiom the removed SignupLayout used, so it is
  the house pattern rather than a new invention. `help-layout-orientation
  .spec.js` asserts the link count as a ceiling: if this grows an article
  list or the app's sections, it has turned back into the chrome the
  change removed.

  NOT ON THE STORE (2026-09-20, white label). The masthead is CONDITIONAL:
  shopper help does not get it. Jake: "The help pages on the store
  shouldn't have the sticky header that links to ThreadFlow. (The header
  can stay on the SA and Distributor help pages.)" A shopper reaches help
  from a storefront wearing the distributor's brand, so naming ThreadFlow
  there -- and offering a click straight to threadflow.com -- leaks the
  distributor's software vendor to their customer.

  The audience comes from `$route.meta.hideBrandMasthead`, set on the two
  shopper help routes in `router/groups/storefront.js`, NOT from the path.
  See `showBrandMasthead` below for why, and for the fail-safe direction
  (anything but an explicit `true` renders it).

  The stranding argument above still holds where it applies: it was always
  about the distributor reached via `$router.push`, and those call sites
  are all in the OWNER subtree, which keeps the masthead. A shopper never
  arrives here from an app shell -- storefront help opens from the
  storefront's own chrome -- so removing it strands nobody.

  The home link targets `/` rather than a role-specific landing. `/` is a
  real top-level route (`name: 'dashboard'`), and the router's own guards
  land each session where it belongs -- so this layout needs no notion of
  who is reading, which matters because it serves BOTH the authenticated
  owner tree and the public shopper tree.

  NOT AN AUTH BOUNDARY. This is presentation only. `/distributor/help/
  owner/*` stays behind distributor auth and `/help/shopper/*` stays
  public; both are enforced by `requireToken`'s path allowlist plus the
  routes' `requireCapability('help.access')` guard, neither of which knows
  this component exists.

  data-testids:
    - help-layout-back-to-app  same-tab link to the app root (not framed)
    - help-layout            root (always)
    - help-layout-masthead   the header (owner + superadmin only)
    - help-layout-home       the brand link (owner + superadmin only)

  Tokens only -- no hardcoded color values.
-->
<template>
    <div class="help-layout" data-testid="help-layout">
        <header
            v-if="showBrandMasthead"
            class="help-layout__masthead"
            data-testid="help-layout-masthead"
        >
            <a
                href="https://threadflow.com/"
                target="_blank"
                rel="noopener noreferrer"
                class="help-layout__brand"
                data-testid="help-layout-home"
            >
                <span class="help-layout__logo">
                    <v-icon icon="polyline" size="16" />
                </span>
                <span class="help-layout__wordmark">ThreadFlow</span>
            </a>
            <!-- The way back INTO the app (Jake, 2026-10-22: change notes
                 were a dead end). A plain same-tab anchor to the app root;
                 the router guards land each session (signed in or out)
                 where it belongs. Not in the flyover: loading the app
                 inside the popover is the 2026-08-26 "disconcerting" bug.
                 The masthead itself is already absent on shopper help. -->
            <a
                v-if="showBackToApp"
                href="/"
                class="help-layout__back"
                data-testid="help-layout-back-to-app"
            >Back to the app</a>
        </header>

        <main class="help-layout__body">
            <slot />
        </main>
    </div>
</template>

<script>
import { isHelpFramed } from "@/router/helpFramed";

export default {
    name: "HelpLayout",

    computed: {
        /**
         * Whether to render the ThreadFlow masthead.
         *
         * WHITE LABEL. Jake, 2026-09-20: "The help pages on the store
         * shouldn't have the sticky header that links to ThreadFlow. (The
         * header can stay on the SA and Distributor help pages.)" A
         * shopper reads help from a storefront that is the DISTRIBUTOR's
         * brand, so a masthead naming ThreadFlow -- and linking out to
         * threadflow.com in a new tab -- tells that shopper who the
         * distributor's software vendor is. Owner and SuperAdmin help are
         * ThreadFlow's own documentation for people who already know, and
         * for them the masthead is also orientation: this surface has no
         * app shell, and three `$router.push` call sites land a
         * distributor here in the same tab (see the file comment).
         *
         * THE AUDIENCE COMES FROM THE ROUTE, not from the path. This
         * layout serves all three audiences and nothing in its own markup
         * distinguishes them, so the route -- the thing that actually
         * differs -- carries the answer in `meta.hideBrandMasthead`
         * (router/groups/storefront.js). Matching on a path prefix would
         * be correct only until a shopper help route moved, and that risk
         * is concrete rather than theoretical: the owner routes are
         * DECLARED `/help/owner` and SERVED `/distributor/help/owner`,
         * because they mount inside `withPrefix('/distributor', ...)`.
         *
         * FAIL SAFE TOWARD SHOWING IT. Anything other than an explicit
         * `true` renders the masthead: no router, no `$route`, no `meta`,
         * or a flag set to something else. Two things follow, both
         * wanted. A help route added without considering audience keeps
         * ThreadFlow's identity rather than silently stripping it, so the
         * dangerous direction requires a deliberate act. And the strict
         * `=== true` means a truthy-but-wrong value -- notably the STRING
         * "false", which is truthy in JS -- cannot hide the brand by
         * accident.
         */
        showBrandMasthead() {
            return this.$route?.meta?.hideBrandMasthead !== true;
        },

        /** Standalone owner/SuperAdmin help only: never in the flyover. */
        showBackToApp() {
            return this.showBrandMasthead && !isHelpFramed(this.$route);
        },
    },
};
</script>

<style scoped>
.help-layout {
    /* Fixed-viewport pattern -- App.vue clamps html/body/.v-application to
     * the viewport and disables document scroll, so a layout shell that
     * does not provide its own scroller silently clips tall content. Help
     * articles are exactly the tall-content case, so the BODY is the
     * scroller and the masthead stays put. */
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--tf-bg);
    color: var(--tf-fg-2);
}

.help-layout__masthead {
    height: 60px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 0 32px;
    background: var(--tf-bg);
    border-bottom: 1px solid var(--tf-border);
}

.help-layout__brand {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
}

.help-layout__back {
    margin-left: auto;
    font-size: 14px;
    color: var(--tf-secondary);
    text-decoration: none;
}

.help-layout__back:hover {
    text-decoration: underline;
}

@media print {
    .help-layout__back {
        display: none;
    }
}

.help-layout__logo {
    width: 28px;
    height: 28px;
    border-radius: var(--tf-radius-sm);
    background: var(--tf-secondary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--tf-fg-on-secondary);
}

.help-layout__wordmark {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-secondary);
    letter-spacing: -0.2px;
}

.help-layout__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}
</style>
